import { createContext, useContext, useEffect, useMemo, useState } from 'react'

const TZ_KEY = 'affisee_timezone'
const LEGACY_TZ_KEY = 'clixtrack_timezone'
const DEFAULT_TIMEZONE = 'Asia/Karachi'
const DEFAULT_MIGRATION_KEY = 'affisee_default_timezone_pk_v1'

const FALLBACK_TIMEZONES = [
  'UTC',
  'Africa/Cairo', 'Africa/Casablanca', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi',
  'America/Anchorage', 'America/Argentina/Buenos_Aires', 'America/Bogota', 'America/Caracas',
  'America/Chicago', 'America/Denver', 'America/Halifax', 'America/Indiana/Indianapolis',
  'America/Los_Angeles', 'America/Mexico_City', 'America/New_York', 'America/Phoenix',
  'America/Sao_Paulo', 'America/St_Johns', 'America/Toronto', 'America/Vancouver',
  'Asia/Baghdad', 'Asia/Baku', 'Asia/Bangkok', 'Asia/Colombo', 'Asia/Dhaka', 'Asia/Dubai',
  'Asia/Hong_Kong', 'Asia/Jakarta', 'Asia/Jerusalem', 'Asia/Karachi', 'Asia/Kathmandu',
  'Asia/Kolkata', 'Asia/Kuala_Lumpur', 'Asia/Manila', 'Asia/Riyadh', 'Asia/Seoul',
  'Asia/Shanghai', 'Asia/Singapore', 'Asia/Tehran', 'Asia/Tokyo',
  'Atlantic/Azores', 'Atlantic/Cape_Verde', 'Atlantic/South_Georgia',
  'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Darwin', 'Australia/Hobart',
  'Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney',
  'Europe/Amsterdam', 'Europe/Athens', 'Europe/Berlin', 'Europe/Istanbul', 'Europe/London',
  'Europe/Madrid', 'Europe/Moscow', 'Europe/Paris', 'Europe/Rome', 'Europe/Warsaw',
  'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Guam', 'Pacific/Honolulu', 'Pacific/Tongatapu',
]

function supportedTimezones() {
  try {
    const zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
    return ['UTC', ...zones.filter((zone) => zone !== 'UTC')]
  } catch {
    return FALLBACK_TIMEZONES
  }
}

function timezoneOffsetMinutes(timeZone, date = new Date()) {
  if (timeZone === 'UTC') return 0
  try {
    const offsetName = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    }).formatToParts(date).find((part) => part.type === 'timeZoneName')?.value || ''
    const match = offsetName.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/i)
    if (match) {
      const minutes = Number(match[2]) * 60 + Number(match[3] || 0)
      return match[1] === '-' ? -minutes : minutes
    }

    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(date).filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]))
    const asUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour) % 24, Number(parts.minute), Number(parts.second))
    return Math.round((asUtc - date.getTime()) / 60000)
  } catch {
    return 0
  }
}

function formatOffset(minutes) {
  const sign = minutes < 0 ? '-' : '+'
  const absolute = Math.abs(minutes)
  const hours = String(Math.floor(absolute / 60)).padStart(2, '0')
  const mins = String(absolute % 60).padStart(2, '0')
  return `GMT${sign}${hours}:${mins}`
}

export function timezoneLabel(timeZone) {
  return `(${formatOffset(timezoneOffsetMinutes(timeZone))}) ${timeZone}`
}

export const TIMEZONES = supportedTimezones()
  .map((value) => ({ value, offset: timezoneOffsetMinutes(value), label: timezoneLabel(value) }))
  .sort((a, b) => a.offset - b.offset || a.value.localeCompare(b.value))

const TimezoneContext = createContext({
  timezone: DEFAULT_TIMEZONE,
  setTimezone: () => {},
})

function validTimezone(value) {
  if (!value) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format()
    return true
  } catch {
    return false
  }
}

function initialTimezone() {
  try {
    const saved = localStorage.getItem(TZ_KEY) || localStorage.getItem(LEGACY_TZ_KEY)
    const migrated = localStorage.getItem(DEFAULT_MIGRATION_KEY)
    if (!migrated) {
      localStorage.setItem(DEFAULT_MIGRATION_KEY, '1')
      if (!saved || saved === 'UTC') return DEFAULT_TIMEZONE
    }
    return validTimezone(saved) ? saved : DEFAULT_TIMEZONE
  } catch {
    return DEFAULT_TIMEZONE
  }
}

export function TimezoneProvider({ children }) {
  const [timezone, setTimezoneState] = useState(initialTimezone)

  const setTimezone = (nextTimezone) => {
    const next = validTimezone(nextTimezone) ? nextTimezone : DEFAULT_TIMEZONE
    try {
      localStorage.setItem(TZ_KEY, next)
      localStorage.setItem(LEGACY_TZ_KEY, next)
    } catch {
      /* storage may be blocked */
    }
    setTimezoneState(next)
    window.dispatchEvent(new CustomEvent('affisee:timezone-change', { detail: next }))
    window.dispatchEvent(new CustomEvent('clixtrack:timezone-change', { detail: next }))
  }

  useEffect(() => {
    try {
      localStorage.setItem(TZ_KEY, timezone)
      localStorage.setItem(LEGACY_TZ_KEY, timezone)
    } catch {
      /* storage may be blocked */
    }
  }, [timezone])

  const value = useMemo(() => ({ timezone, setTimezone }), [timezone])
  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>
}

export function useTimezone() {
  return useContext(TimezoneContext)
}
